import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirm } from "material-ui-confirm";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { RootState } from "../../../app/store";
import { commandActions, CommandObject } from "../../../redux/command/commandSlice";
import { toDate, sameDay, hour, displayDate } from "../../../utils/TimeUtils";
import { RELAY_1_ID, RELAY_2_ID, RELAY_3_ID, BUZZER_1_ID } from "../../../config/command";
import DeviceCard from "./DeviceCard";
import VoltageCard from "./VoltageCard";
import SignalIcon from "./SignalIcon";

export type RemoteDevice = { // a relay or a buzzer
    id: string,
    name: string,
    state: number,
    pending: number,
    source: string
};

const RELAYS = [
    {id: RELAY_1_ID, name: "Relay 1"},
    {id: RELAY_2_ID, name: "Relay 2"},
    {id: RELAY_3_ID, name: "Relay 3"}
];

const BUZZERS = [
    {id: BUZZER_1_ID, name: "Buzzer"}
];


export const deviceState = (kind: string, frame: any, history: CommandObject[], id: string, name: string, source: string) => {
    const lastCommand = history.filter(command => command.parameters?.id ? command.parameters.id === id : command.type === kind).slice(-1)[0];
    return {
        id,
        name,
        state: frame?.[id] !== undefined && Number(frame[id]) !== undefined ? Number(frame[id]) : -1,
        pending: lastCommand?.parameters?.state !== undefined ? lastCommand.parameters.state : -1,
        source
    };
};

const CnesCommandTab = ({source, label, name}: {source: string, label: string, name: string}) => {
    const confirm = useConfirm();
    const dispatch = useDispatch();

    const now = Math.round(Date.now() / 1000);

    const commands = useSelector((state: RootState) => state.command.commands);
    const commandHistory = Object.keys(commands).map(key => commands[key]).filter(command => command.source === source);

    const [frame, updated] = useSelector((state: RootState) => {
        const currentTime = state.replay.currentTime;
        const currentTS = toDate(currentTime).getTime() / 1000;

        const stream = state.telemetry.streams[source]?.[label];
        const currentTimestamps = currentTS ? stream[0].filter(timestamp => timestamp <= currentTS) : stream[0];
        const currentStream = [currentTimestamps, stream[1].slice(0, currentTimestamps.length)];

        const frame = currentStream?.[1] ? currentStream[1].slice(-1)[0] : null; // getting last data frame from stream
        const updated = currentStream?.[0]?.[0] ? toDate(currentStream[0].slice(-1)[0]) : null;

        return [frame, updated];
    });

    const [relays, setRelays] = useState<RemoteDevice[]>(RELAYS.map((relay) => deviceState("relay", frame, commandHistory, relay.id, relay.name, source)));
    const [buzzers, setBuzzers] = useState<RemoteDevice[]>(BUZZERS.map((buzzer) => deviceState("buzzer", frame, commandHistory, buzzer.id, buzzer.name, source)));

    useEffect(() => {
        const history = Object.keys(commands).map(key => commands[key]).filter(command => command.source === source);
        // ^to prevent re-render loop tiggered by 'commandHistory' redefinition on each render
        setRelays(RELAYS.map((relay) => deviceState("relay", frame, history, relay.id, relay.name, source)));
        setBuzzers(BUZZERS.map((buzzer) => deviceState("buzzer", frame, history, buzzer.id, buzzer.name, source)));
    }, [commands, frame, source]);

    const relayCommand = useCallback((destination: string, relayId: string, state: number) => {
        confirm({
            confirmationButtonProps: {color: "inherit"},
            cancellationButtonProps: {color: "inherit"},
            description: `Set '${relayId}' to ${state ? "'Actif'" : "'Repos'"} ?`
        }).then(() => {
            dispatch(commandActions.send_relay_command({destination, relayId, state}));
        });
    }, [confirm, dispatch]);

    const buzzerCommand = useCallback((destination: string, buzzerId: string, frequency: number) => {
        confirm({
            confirmationButtonProps: {color: "inherit"},
            cancellationButtonProps: {color: "inherit"},
            description: `Turn the buzzer ${frequency ? "on" : "off"} ?`
        }).then(() => {
            dispatch(commandActions.send_buzzer_command({destination, buzzerId, frequency}));
        });
    }, [confirm, dispatch]);

    if (!frame) {
        return (<Box sx={{ p: 4, width: "100%", overflow: "scroll" }}>
            <Typography variant="h4" gutterBottom>{name} FTS Dashboard</Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>No data available</Typography>
        </Box>)
    } else {
        return (
            <Box sx={{ p: 4, width: "100%", overflow: "scroll" }}>
                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="column" spacing={0}>
                        <Typography variant="h4">{name} FTS Dashboard</Typography>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Last updated: {updated ?
                                sameDay(now, updated) ?
                                    hour(updated)
                                :
                                    `${displayDate(updated)} - ${hour(updated)}`
                            :
                                "-"
                            }
                        </Typography>
                    </Stack>
                    <Stack direction="column" sx={{marginRight: 5, alignItems: "center"}}>
                        <SignalIcon signalBars={frame.signalBars}/>
                        <Typography variant="overline" marginTop={1.5}>Signal: {frame.signalBars}/5</Typography>
                    </Stack>
                </Stack>
                <Grid container columnSpacing={1} rowSpacing={1}>
                    {relays.map(relay => (
                        <Grid item xs={12} md={6} xl={3} key={relay.id}>
                            <DeviceCard kind="relay" device={relay} deviceCommand={relayCommand} commandHistory={commandHistory} source={source} label={label} name={name}/>
                        </Grid>
                    ))}
                    {buzzers.map(buzzer => (
                        <Grid item xs={12} md={6} xl={3} key={buzzer.id}>
                            <DeviceCard kind="buzzer" device={buzzer} deviceCommand={buzzerCommand} commandHistory={commandHistory} source={source} label={label} name={name}/>
                        </Grid>
                    ))}
                    <Grid item xs={12} md={6} xl={2}>
                        <VoltageCard voltage={frame.batteryVoltage} source={source} label={label} name={name}/>
                    </Grid>
                </Grid>
            </Box>
        );
    }
};

export default CnesCommandTab;